import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { logsAPI, chatAPI, diagnosticLogsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ScrollText, Power, ChevronDown, ChevronRight, MessageSquare,
  Loader2, RefreshCw, AlertCircle, Trash2, Bug, Filter, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';
import { PHASE_COLORS, EVENT_LABELS } from '@/components/autopsy/autopsyConstants';
import EventDetailRenderer from '@/components/autopsy/EventDetailRenderer';
import AutopsyConfigReport from '@/components/autopsy/AutopsyConfigReport';
import AutopsyHarnessWorkflow from '@/components/autopsy/AutopsyHarnessWorkflow';
import { soloHoraConSegundos, fechaYHoraConSegundos } from '../lib/fechas';

const DIAG_ORIGINS = ['', 'backend', 'frontend', 'mongodb', 'qdrant', 'whatsapp-bridge', 'external', 'unknown'];

/** Limite aproximado para portapapeles (evita bloqueos del navegador en autopsias enormes). */
const MAX_AUTOPSY_CLIPBOARD_CHARS = 400_000;

function buildAutopsyClipboardText(conversationId, events) {
  let out = `# OmniSyS autopsia conversacion\nconversation_id: ${conversationId}\nfecha_export: ${new Date().toISOString()}\neventos: ${events.length}\n\n`;
  (events || []).forEach((ev, i) => {
    const label = EVENT_LABELS[ev.event_type] || ev.event_type || '';
    out += `--- evento ${i + 1} ---\n`;
    out += `timestamp: ${ev.timestamp || ''}\n`;
    out += `phase: ${ev.phase || ''}\n`;
    out += `event_type: ${ev.event_type || ''}\n`;
    out += `titulo_ui: ${label}\n`;
    out += `description: ${String(ev.description || '').replace(/\r/g, '')}\n`;
    const payload = { ...ev };
    delete payload._id;
    out += `detalle_json:\n${JSON.stringify(payload, null, 2)}\n\n`;
  });
  return out;
}

/** Etiquetas para acciones enviadas por whatsapp-bridge a diagnostic_logs */
const WA_BRIDGE_ACTION_LABELS = {
  'wa.bridge.wa_messages_upsert': 'WA bridge: Baileys messages.upsert',
  'wa.bridge.wa_messages_upsert_non_notify': 'WA bridge: upsert no-notify (no reenviado)',
  'wa.bridge.wa_inbound_summary': 'WA bridge: mensaje entrante (resumen)',
  'wa.bridge.wa_inbound_skipped': 'WA bridge: mensaje omitido',
  'wa.bridge.wa_forward_backend_ok': 'WA bridge: POST webhook OK',
  'wa.bridge.wa_forward_backend_failed': 'WA bridge: POST webhook fallo',
  'wa.bridge.wa_baileys_connection': 'WA bridge: conexion Baileys',
  'auth.oidc.microsoft.start': 'Auth OIDC Microsoft: inicio',
  'auth.oidc.microsoft.callback': 'Auth OIDC Microsoft: callback',
};

function formatDiagnosticAction(action) {
  if (!action) return '';
  return WA_BRIDGE_ACTION_LABELS[action] || action;
}

function DiagnosticErrorsTab({ user }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [origin, setOrigin] = useState('');
  const [capsuleId, setCapsuleId] = useState('');
  const [level, setLevel] = useState('');
  // Vacío = todas; 'todas' = sólo fallas de herramienta, sin importar el tipo.
  const [tipoFalla, setTipoFalla] = useState('');
  const [catalogoFallas, setCatalogoFallas] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [purgeArmed, setPurgeArmed] = useState(false);

  // El catálogo lo manda el servidor, no lo tiene escrito la pantalla: si el backend agrega
  // un tipo, aparece acá solo. Al revés habría tipos que se registran y no se pueden filtrar,
  // o sea fallas invisibles desde la única pantalla que existe para verlas.
  useEffect(() => {
    let vivo = true;
    diagnosticLogsAPI.tiposDeFalla()
      .then((r) => { if (vivo) setCatalogoFallas(r.data); })
      .catch(() => { if (vivo) setCatalogoFallas({ tipos: [], prefijo: 'herramienta.falla.' }); });
    return () => { vivo = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, skip: 0 };
      if (origin) params.origin = origin;
      if (capsuleId.trim()) params.capsule_id = capsuleId.trim();
      if (level) params.level = level;
      const prefijo = catalogoFallas?.prefijo || 'herramienta.falla.';
      const prefijoCanal = catalogoFallas?.no_atendidos?.prefijo || 'canal.no_atendido.';
      if (tipoFalla === 'todas') params.action_prefix = prefijo;
      else if (tipoFalla === 'canal:todas') params.action_prefix = prefijoCanal;
      else if (tipoFalla.startsWith('canal:')) params.action = `${prefijoCanal}${tipoFalla.slice(6)}`;
      else if (tipoFalla) params.action = `${prefijo}${tipoFalla}`;
      const res = await diagnosticLogsAPI.list(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar los logs');
    } finally {
      setLoading(false);
    }
  }, [origin, capsuleId, level, tipoFalla, catalogoFallas]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setPurgeArmed(false);
  }, [origin, capsuleId, level, tipoFalla]);

  const handleDelete = async (logId) => {
    try {
      await diagnosticLogsAPI.delete(logId);
      toast.success('Registro eliminado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const handlePurgeAll = async () => {
    if (!purgeArmed) {
      setPurgeArmed(true);
      toast.info('Pulsa de nuevo el boton para confirmar el vaciado');
      return;
    }
    setPurgeArmed(false);
    try {
      const res = await diagnosticLogsAPI.purge({});
      toast.success(`Eliminados: ${res.data.deleted}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo vaciar');
    }
  };

  const toggleRow = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  return (
    <div className="space-y-4" data-testid="diagnostic-logs-tab">
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-2">
              <Bug className="w-3.5 h-3.5" /> Errores e incidencias (MongoDB)
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={load} disabled={loading}>
                <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`h-7 text-xs rounded-sm border-red-300 ${purgeArmed ? 'bg-red-50 text-red-800' : 'text-red-700'}`}
                onClick={handlePurgeAll}
              >
                {purgeArmed ? 'Confirmar vaciado' : 'Vaciar todo'}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1">
            Tenant: <span className="font-mono text-zinc-600">{user?.tenant_id}</span>
            {' · '}
            Total: <strong>{total}</strong>
          </p>
        </CardHeader>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <span className="text-[10px] uppercase text-zinc-400 flex items-center gap-1"><Filter className="w-3 h-3" /> Origen</span>
              <select
                className="border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
              >
                {DIAG_ORIGINS.map((o) => (
                  <option key={o || 'all'} value={o}>{o || '(todos)'}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="text-[10px] uppercase text-zinc-400">Capsula</span>
              <Input
                className="h-8 text-xs rounded-sm w-32 mt-0.5"
                placeholder="vacío = todas"
                value={capsuleId}
                onChange={(e) => setCapsuleId(e.target.value)}
              />
            </div>
            <div>
              <span className="text-[10px] uppercase text-zinc-400">Nivel</span>
              <select
                className="border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <option value="">(todos)</option>
                <option value="error">error</option>
                <option value="warn">warn</option>
                <option value="info">info</option>
              </select>
            </div>
            <div>
              <span className="text-[10px] uppercase text-zinc-400">Tipo de falla</span>
              <select
                data-testid="filtro-tipo-de-falla"
                className="border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 max-w-[16rem]"
                value={tipoFalla}
                onChange={(e) => setTipoFalla(e.target.value)}
              >
                <option value="">(todo, no solo herramientas)</option>
                <option value="todas">— solo fallas de herramienta —</option>
                {(catalogoFallas?.tipos || []).map((t) => (
                  // El conteo va al lado a propósito, y un tipo en 0 se muestra igual: que
                  // una falla NO esté pasando también es información, y un filtro que sólo
                  // lista lo ocurrido no deja verla.
                  <option key={t.id} value={t.id} title={`${t.que}. ${t.hacer}`}>
                    {t.id} ({t.n}) · {t.quien}
                  </option>
                ))}
                {/* Lo que un canal no atendió por sus reglas de remitentes (2026-10-14). */}
                <optgroup label="El canal no atendió">
                  <option value="canal:todas">— todo lo que un canal no atendió —</option>
                  {(catalogoFallas?.no_atendidos?.tipos || []).map((t) => (
                    <option key={t.id} value={`canal:${t.id}`} title={t.que}>
                      {t.id.replaceAll('_', ' ')} ({t.n})
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>
          {tipoFalla && (
            <p className="text-[11px] text-zinc-500">
              {tipoFalla === 'todas'
                ? 'Todas las fallas de ejecución de herramienta, de cualquier tipo.'
                : tipoFalla === 'canal:todas'
                ? 'Remitentes que un canal descartó en silencio por sus reglas de «Quién atiende este canal».'
                : tipoFalla.startsWith('canal:')
                ? (() => {
                    const f = (catalogoFallas?.no_atendidos?.tipos || []).find((t) => `canal:${t.id}` === tipoFalla);
                    return f ? <>El canal no lo atendió porque {f.que}.</> : null;
                  })()
                : (() => {
                    const f = (catalogoFallas?.tipos || []).find((t) => t.id === tipoFalla);
                    return f ? <><strong>{f.que}.</strong> {f.hacer} <span className="text-zinc-400">— responsable: {f.quien}</span></> : null;
                  })()}
            </p>
          )}
        </CardContent>
      </Card>

      {loading && items.length === 0 ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
      ) : items.length === 0 ? (
        <p className="text-xs text-zinc-400 text-center py-8">Sin registros con los filtros actuales</p>
      ) : (
        <div className="space-y-2 max-h-[68vh] overflow-auto pr-1">
          {items.map((row) => {
            const open = expanded.has(row.log_id);
            return (
              <Card key={row.log_id} className="border border-brand-border rounded-sm overflow-hidden">
                <div className="flex items-start gap-2 p-3 bg-white">
                  <button type="button" onClick={() => toggleRow(row.log_id)} className="mt-0.5 text-zinc-400">
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="text-[9px]">{row.origin}</Badge>
                      {row.capsule_id && <Badge className="text-[9px] bg-zinc-100">{row.capsule_id}</Badge>}
                      {row.http_status != null && <Badge className="text-[9px] bg-amber-50 text-amber-900">HTTP {row.http_status}</Badge>}
                      {/* Cuando es una falla de herramienta, las dos cosas que se buscan
                          primero: cuál falló y de quién es. Salen del `detail` del propio
                          registro, no de un catálogo del front. */}
                      {row.detail?.tipo && (
                        <Badge className="text-[9px] bg-red-50 text-red-800 font-mono">{row.detail.tipo}</Badge>
                      )}
                      {row.detail?.quien && (
                        <Badge variant="outline" className="text-[9px]">es de: {row.detail.quien}</Badge>
                      )}
                      {row.detail?.herramienta && (
                        <Badge className="text-[9px] bg-zinc-100 font-mono">{row.detail.herramienta}</Badge>
                      )}
                      <span className="text-[10px] text-zinc-400 font-mono">
                        {fechaYHoraConSegundos(row.created_at, row.created_at)}
                      </span>
                    </div>
                    <p className="text-xs font-medium text-[#09090B] mt-1 truncate" title={row.action}>
                      {formatDiagnosticAction(row.action)}
                    </p>
                    <p className="text-[11px] text-zinc-600 mt-0.5 line-clamp-2">{row.message}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-zinc-300 hover:text-red-600 shrink-0"
                    title="Eliminar"
                    onClick={() => handleDelete(row.log_id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {open && (
                  <div className="px-3 pb-3 border-t border-zinc-100 bg-zinc-50/80">
                    <pre className="text-[10px] font-mono text-zinc-700 whitespace-pre-wrap overflow-auto max-h-64 mt-2 p-2 bg-white border border-zinc-200 rounded-sm">
                      {JSON.stringify(row.detail != null ? row.detail : { log_id: row.log_id, meta: { action: row.action, message: row.message } }, null, 2)}
                    </pre>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function LogsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTab = searchParams.get('tab') || 'diagnostic';
  const urlConversationId = searchParams.get('conversation') || null;

  const [enabled, setEnabled] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(urlConversationId);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [autopsyView, setAutopsyView] = useState('timeline');
  const [capsuleFilter, setCapsuleFilter] = useState('');
  const [openConvInput, setOpenConvInput] = useState(urlConversationId || '');

  const loadStatus = useCallback(async () => {
    try {
      const res = await logsAPI.status();
      setEnabled(res.data.enabled);
    } catch (err) { console.error(err); }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await chatAPI.listConversations();
      setConversations(res.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadStatus(); loadConversations(); }, [loadStatus, loadConversations]);

  useEffect(() => {
    if (urlConversationId && urlConversationId !== selectedConvId) {
      setSelectedConvId(urlConversationId);
      loadConversationLogs(urlConversationId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConversationId]);

  const setMainTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const selectConversation = (convId) => {
    setSelectedConvId(convId);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'autopsy');
    next.set('conversation', convId);
    setSearchParams(next, { replace: true });
    loadConversationLogs(convId);
  };

  const filteredConversations = capsuleFilter.trim()
    ? conversations.filter((c) => (c.capsule_id || '').includes(capsuleFilter.trim()))
    : conversations;

  const handleToggle = async (val) => {
    try {
      await logsAPI.toggle(val);
      setEnabled(val);
      toast.success(val ? 'Logging activado' : 'Logging desactivado');
    } catch (err) { toast.error('Error'); }
  };

  const loadConversationLogs = async (convId) => {
    setLoading(true);
    setExpandedEvents(new Set());
    try {
      const params = {};
      if (capsuleFilter.trim()) params.capsule_id = capsuleFilter.trim();
      const res = await logsAPI.conversation(convId, params);
      setEvents(res.data.events || []);
    } catch (err) { setEvents([]); }
    finally { setLoading(false); }
  };

  const toggleEvent = (idx) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const expandAll = () => setExpandedEvents(new Set(events.map((_, i) => i)));
  const collapseAll = () => setExpandedEvents(new Set());

  const handleCopyAllAutopsy = async () => {
    if (!selectedConvId || !events.length) return;
    let text = buildAutopsyClipboardText(selectedConvId, events);
    let truncated = false;
    if (text.length > MAX_AUTOPSY_CLIPBOARD_CHARS) {
      text = text.slice(0, MAX_AUTOPSY_CLIPBOARD_CHARS);
      truncated = true;
      text += `\n\n[TRUNCADO: el texto superaba ${MAX_AUTOPSY_CLIPBOARD_CHARS} caracteres; exporta por partes o reduce la conversacion en logs.]\n`;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(truncated ? 'Autopsia copiada (truncada por tamano)' : 'Autopsia copiada al portapapeles');
    } catch {
      toast.error('No se pudo copiar (permisos del navegador o portapapeles)');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="logs-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[#09090B]">Logs</h1>
          <p className="text-xs text-zinc-400 mt-1 uppercase tracking-[0.15em]">
            Autopsia por conversacion y registro de errores del sistema
          </p>
        </div>
        <HelpButton section="logs" />
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="mb-4 grid w-full max-w-xl grid-cols-2 gap-1 bg-zinc-100 p-1 rounded-sm border border-brand-border h-auto">
          <TabsTrigger
            value="diagnostic"
            className="text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-brand-primary data-[state=active]:shadow-sm rounded-sm py-2"
          >
            Errores e incidencias (BD)
          </TabsTrigger>
          <TabsTrigger
            value="autopsy"
            className="text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-brand-primary data-[state=active]:shadow-sm rounded-sm py-2"
          >
            Autopsia conversaciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="diagnostic" className="mt-0">
          <DiagnosticErrorsTab user={user} />
        </TabsContent>

        <TabsContent value="autopsy" className="mt-0">
      <div className="flex items-center justify-end gap-3 mb-4">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-brand-border rounded-sm bg-white">
            <Power className={`w-3.5 h-3.5 ${enabled ? 'text-green-500' : 'text-zinc-400'}`} />
            <span className="text-xs font-medium">{enabled ? 'Activo' : 'Inactivo'}</span>
            <Switch data-testid="log-toggle" checked={enabled} onCheckedChange={handleToggle} />
          </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Conversation list */}
        <div className="col-span-4">
          <Card className="border border-brand-border rounded-sm">
            <CardHeader className="pb-2 border-b border-brand-border">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                  <ScrollText className="w-3.5 h-3.5 inline mr-1" /> Conversaciones
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={loadConversations} className="h-7 w-7 p-0">
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
              <Input
                className="h-7 text-xs mt-2 rounded-sm"
                placeholder="Filtrar capsule_id..."
                value={capsuleFilter}
                onChange={(e) => setCapsuleFilter(e.target.value)}
              />
              <div className="mt-2 space-y-1">
                <p className="text-[10px] text-zinc-500 leading-snug">
                  La lista solo muestra hilos omnicanal. Para autopsia de Asistente de ticket u otros IDs,
                  abrí el <code className="bg-zinc-100 px-0.5">conversation_id</code> directo
                  (ej. <code className="bg-zinc-100 px-0.5">ticket-chat-20260724-6</code>).
                </p>
                <div className="flex gap-1">
                  <Input
                    className="h-7 text-xs rounded-sm font-mono"
                    placeholder="Abrir conversation_id..."
                    value={openConvInput}
                    onChange={(e) => setOpenConvInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && openConvInput.trim()) {
                        selectConversation(openConvInput.trim());
                      }
                    }}
                    data-testid="logs-open-conversation-id"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px] rounded-sm shrink-0"
                    disabled={!openConvInput.trim()}
                    onClick={() => selectConversation(openConvInput.trim())}
                    data-testid="logs-open-conversation-btn"
                  >
                    Abrir
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 max-h-[65vh] overflow-auto">
              {filteredConversations.length === 0 ? (
                <p className="text-xs text-zinc-400 text-center py-8">No hay conversaciones</p>
              ) : (
                filteredConversations.map(conv => {
                  const lastMsg = conv.messages?.[0];
                  const isSelected = selectedConvId === conv.conversation_id;
                  return (
                    <button
                      key={conv.conversation_id}
                      data-testid={`log-conv-${conv.conversation_id}`}
                      onClick={() => selectConversation(conv.conversation_id)}
                      className={`w-full text-left p-3 border-b border-zinc-100 transition-colors hover:bg-zinc-50 ${isSelected ? 'bg-blue-50/50 border-l-2 border-brand-primary' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-[#09090B] truncate max-w-[180px]">
                          {lastMsg?.content?.substring(0, 40) || conv.conversation_id.substring(0, 12)}...
                        </span>
                        <Badge variant="outline" className="text-[9px] rounded-sm">{conv.channel || 'webchat'}</Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-zinc-400">{conv.current_agent?.agent_id}</span>
                        {conv.capsule_id && <Badge variant="outline" className="text-[8px] rounded-sm">{conv.capsule_id}</Badge>}
                        {conv.label && <Badge className="text-[9px] rounded-sm bg-zinc-100 text-zinc-600">{conv.label}</Badge>}
                        <span className="text-[10px] text-zinc-400 ml-auto">{conv.status}</span>
                      </div>
                      <span className="text-[9px] font-mono text-zinc-300">{conv.conversation_id.substring(0, 8)}</span>
                    </button>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <div className="col-span-8">
          {!selectedConvId ? (
            <Card className="border border-brand-border rounded-sm">
              <CardContent className="p-12 text-center">
                <ScrollText className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
                <p className="text-sm text-zinc-400">Selecciona una conversacion para ver la autopsia completa</p>
                <p className="text-xs text-zinc-300 mt-1">Cada evento detalla que paso, por que, y como en cada etapa del procesamiento</p>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex gap-1 bg-zinc-100 p-0.5 rounded-sm border border-zinc-200">
                  {['timeline', 'workflow', 'report', 'coverage'].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAutopsyView(v)}
                      className={`text-[10px] px-2 py-1 rounded-sm ${autopsyView === v ? 'bg-white shadow-sm text-brand-primary font-semibold' : 'text-zinc-500'}`}
                    >
                      {v === 'timeline'
                        ? 'Timeline'
                        : v === 'workflow'
                          ? 'Workflow'
                          : v === 'report'
                            ? 'Informe Config'
                            : 'Mapa cobertura'}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-zinc-500 font-mono">{events.length} eventos</span>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyAllAutopsy}
                    className="rounded-sm text-xs h-7"
                    data-testid="autopsy-copy-all"
                    disabled={!events.length}
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copiar todo
                  </Button>
                  <Button variant="outline" size="sm" onClick={expandAll} className="rounded-sm text-xs h-7">Expandir todo</Button>
                  <Button variant="outline" size="sm" onClick={collapseAll} className="rounded-sm text-xs h-7">Colapsar</Button>
                  <Button variant="outline" size="sm" onClick={() => loadConversationLogs(selectedConvId)} className="rounded-sm text-xs h-7">
                    <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
                  </Button>
                </div>
              </div>

              {autopsyView === 'workflow' ? (
                <Card className="border border-brand-border rounded-sm">
                  <CardContent className="p-4">
                    <AutopsyHarnessWorkflow events={events} />
                  </CardContent>
                </Card>
              ) : autopsyView !== 'timeline' ? (
                <Card className="border border-brand-border rounded-sm">
                  <CardContent className="p-4">
                    <AutopsyConfigReport
                      conversationId={selectedConvId}
                      viewMode={autopsyView === 'coverage' ? 'coverage' : 'report'}
                    />
                  </CardContent>
                </Card>
              ) : events.length === 0 ? (
                <Card className="border border-brand-border rounded-sm">
                  <CardContent className="p-8 text-center">
                    <AlertCircle className="w-6 h-6 text-zinc-300 mx-auto mb-2" />
                    <p className="text-xs text-zinc-400">No hay eventos registrados para esta conversacion</p>
                    <p className="text-[10px] text-zinc-300 mt-1">Los eventos se registran cuando el logging esta activo</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-1 max-h-[70vh] overflow-auto pr-1">
                  {events.map((ev, i) => {
                    const phase = PHASE_COLORS[ev.phase] || PHASE_COLORS.custom;
                    const PhaseIcon = phase.icon;
                    const isExpanded = expandedEvents.has(i);
                    const time = ev.timestamp ? soloHoraConSegundos(ev.timestamp, '') : '';

                    return (
                      <div key={i} data-testid={`log-event-${i}`}>
                        {/* Timeline connector */}
                        <div className="flex">
                          <div className="w-10 flex flex-col items-center">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${phase.bg} border ${phase.border}`}>
                              <PhaseIcon className={`w-3 h-3 ${phase.text}`} />
                            </div>
                            {i < events.length - 1 && <div className="w-px flex-1 bg-zinc-200 my-1" />}
                          </div>

                          <div className="flex-1 ml-2 mb-2">
                            <button onClick={() => toggleEvent(i)} className="w-full text-left">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown className="w-3 h-3 text-zinc-400" /> : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                                <Badge className={`text-[9px] rounded-sm ${phase.bg} ${phase.text} border ${phase.border}`}>
                                  {ev.phase?.toUpperCase()}
                                </Badge>
                                <span className="text-xs font-medium text-[#09090B]">
                                  {EVENT_LABELS[ev.event_type] || ev.event_type}
                                </span>
                                <span className="text-[10px] font-mono text-zinc-400 ml-auto">{time}</span>
                              </div>
                              <p className="text-xs text-zinc-600 mt-0.5 ml-5">{ev.description}</p>
                            </button>

                            {/* Expanded detail */}
                            {isExpanded && (
                              <div className={`mt-2 ml-5 p-3 rounded-sm border ${phase.border} ${phase.bg} bg-opacity-30`}>
                                <EventDetailRenderer ev={ev} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
